import React, { useState, useCallback, useRef } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';

const JSON_URL = '/import/empresas_publicas_catastro.json';
const JSON_URL_FALLBACK = '/empresas_publicas_catastro.json';
const BATCH_SIZE = 500;
const PREVIEW_ROWS = 20;

/**
 * JSON estándar no admite NaN, Infinity ni undefined. Excel/JS a veces exportan esos literales y el archivo deja de ser JSON válido.
 */
function sanitizeNonStandardJsonTokens(text) {
  if (typeof text !== 'string' || !text.length) return text;
  const s = text.replace(/^\uFEFF/, '');
  return s
    .replace(/:\s*NaN\b/g, ': null')
    .replace(/,\s*NaN\b/g, ', null')
    .replace(/\[\s*NaN\b/g, '[ null')
    .replace(/:\s*-Infinity\b/g, ': null')
    .replace(/:\s*\+Infinity\b/g, ': null')
    .replace(/:\s*Infinity\b/g, ': null')
    .replace(/:\s*undefined\b/g, ': null');
}

function parseJsonImport(text) {
  const raw = typeof text === 'string' ? text : String(text ?? '');
  try {
    return JSON.parse(raw);
  } catch (e1) {
    try {
      return JSON.parse(sanitizeNonStandardJsonTokens(raw));
    } catch {
      const hint =
        /NaN|Infinity|undefined/i.test(raw)
          ? ' Si el archivo viene de Excel/JS, suele incluir NaN en campos vacíos: vuelve a exportar o guarda esos valores como null o "".'
          : '';
      throw new Error((e1?.message || 'JSON inválido') + hint);
    }
  }
}

/**
 * Orden canónico de campos en el JSON de importación (admin / empresas):
 * nombre, direccion, Region, Comuna, telefono, sitio_web, horario_atencion, descripcion, email, rubro, lat, lng
 * Si Region o Comuna vienen vacíos o ausentes → string vacío.
 * Se aceptan alias y mayúsculas para compatibilidad con archivos antiguos.
 */
function normalizeRow(row) {
  if (!row || typeof row !== 'object') return row;
  const get = (...keys) => {
    for (const k of keys) {
      if (k in row && row[k] != null && row[k] !== '') return row[k];
    }
    return '';
  };
  const str = (v) => {
    if (v == null || (typeof v === 'number' && !Number.isFinite(v))) return '';
    return String(v).trim();
  };
  const coord = (v) => {
    if (v == null || v === '') return '';
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const s = String(v).trim().replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : str(v);
  };

  return {
    nombre: str(get('nombre', 'Nombre')),
    direccion: str(get('direccion', 'Direccion', 'dirección', 'Dirección', 'address', 'sucursal')),
    region: str(get('Region', 'region', 'REGION', 'Región', 'región', 'region_empresa')),
    comuna: str(get('Comuna', 'comuna', 'COMUNA', 'comuna_empresa')),
    telefono: str(get('telefono', 'Telefono Fijo', 'phone', 'Telefono', 'teléfono')),
    sitio_web: str(get('sitio_web', 'web', 'Pagina Web', 'página web', 'pagina_web')),
    horario_atencion: str(get('horario_atencion', 'Horario', 'horario')),
    descripcion: str(get('descripcion', 'Descripción', 'description')),
    email: str(get('email', 'Email', 'correo')),
    rubro: str(get('rubro', 'Rubro')),
    lat: coord(row.lat ?? row.Lat ?? row.latitud),
    lng: coord(row.lng ?? row.Lng ?? row.longitud ?? row.lon),
  };
}

// Dirección en bruto (para guardar en Firestore)
function getDireccionRaw(row) {
  const r = normalizeRow(row);
  const v = r.direccion ?? r.dirección ?? r.address ?? r.sucursal ?? r.direccion_sucursal ?? row.Direccion ?? row.direccion ?? '';
  return typeof v === 'string' ? v : String(v);
}

// Dirección normalizada para comparar duplicados (mismo nombre + misma dirección = duplicado)
function getDireccionNorm(row) {
  return getDireccionRaw(row).toLowerCase().trim();
}

// Huella única: mismo nombre + misma dirección + mismo teléfono = duplicado real.
// Mismo nombre pero distinta dirección o distinto teléfono = sucursales (ej. PRT), se permiten.
// Si la dirección viene vacía, se usa index para no colapsar sucursales con mismo nombre.
function fingerprint(row, index) {
  const n = (row.nombre || '').toString().toLowerCase().trim();
  const d = getDireccionNorm(row);
  const t = (row.telefono || row.phone || '').toString().toLowerCase().trim();
  const lat = row.lat != null ? String(row.lat) : '';
  const lng = row.lng != null ? String(row.lng) : '';
  const base = `${n}|${d}|${t}|${lat}|${lng}`;
  return d ? base : `${base}|#${index}`;
}

function toFirestoreDoc(row) {
  const categorias = Array.isArray(row.categorias) ? row.categorias : [];
  const categoria = typeof row.categoria === 'string' ? row.categoria : categorias.join(', ');
  const servicios = Array.isArray(row.servicios) ? row.servicios : [...categorias];
  const marcas = Array.isArray(row.marcas) ? row.marcas : [];
  const web = row.sitio_web || row.web || '';
  const regionVal = row.region != null ? String(row.region).trim() : '';
  const comunaVal = row.comuna != null ? String(row.comuna).trim() : '';

  return {
    nombre: row.nombre ?? '',
    direccion: getDireccionRaw(row),
    region: regionVal,
    comuna: comunaVal,
    region_empresa: regionVal,
    comuna_empresa: comunaVal,
    telefono: row.telefono ?? row.phone ?? '',
    email: row.email ?? '',
    sitio_web: web,
    web,
    horario_atencion: row.horario_atencion ?? '',
    horarios: row.horarios ?? null,
    rubro: row.rubro ?? '',
    descripcion: row.descripcion ?? '',
    lat: row.lat ?? '',
    lng: row.lng ?? '',
    estado: 'ingresada',
    tipoEmpresa: 'proveedor',
    tipo_empresa: 'publica',
    es_comunidad: false,
    creado_por: 'admin',
    categoria,
    categorias,
    servicios,
    marcas,
    logo: row.logo ?? '',
    logo_url: row.logo_url ?? '',
    fecha_creacion: serverTimestamp(),
    fecha_actualizacion: serverTimestamp(),
  };
}

export default function ImportarEmpresasPublicasJson({ onClose, onSuccess }) {
  const [step, setStep] = useState('idle'); // idle | loading | preview | importing | done
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [empresasToInsert, setEmpresasToInsert] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [progress, setProgress] = useState({ batch: 0, totalBatches: 0, inserted: 0, skipped: 0, failed: 0 });
  const [summary, setSummary] = useState(null);
  const [importSourceLabel, setImportSourceLabel] = useState('');
  const fileInputRef = useRef(null);

  const processParsedJson = useCallback((data) => {
    const rawList = data.empresas ?? data.data ?? data.listado ?? (Array.isArray(data) ? data : []);
    if (!Array.isArray(rawList) || !rawList.length) {
      setError('El JSON no contiene un array (busca "empresas", "data" o "listado", o un array en la raíz).');
      setStep('idle');
      return false;
    }
    const list = rawList.map(normalizeRow);

    const seen = new Set();
    const toInsert = [];
    const dupeList = [];
    list.forEach((row, index) => {
      const fp = fingerprint(row, index);
      if (seen.has(fp)) {
        dupeList.push({ ...row, fingerprint: fp });
        return;
      }
      seen.add(fp);
      toInsert.push(row);
    });

    setRawData(data);
    setEmpresasToInsert(toInsert);
    setDuplicates(dupeList);
    setStep('preview');
    return true;
  }, []);

  const loadJson = useCallback(async () => {
    setStep('loading');
    setError(null);
    setRawData(null);
    setEmpresasToInsert([]);
    setDuplicates([]);
    setImportSourceLabel('JSON del sitio (hosting)');
    const cacheBust = `?t=${Date.now()}&r=${Math.random().toString(36).slice(2)}`;
    const fetchOpts = {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    };
    try {
      let res = await fetch(JSON_URL + cacheBust, fetchOpts);
      if (!res.ok) res = await fetch(JSON_URL_FALLBACK + cacheBust, fetchOpts);
      if (!res.ok) throw new Error(`No se pudo cargar el JSON (${res.status}). Coloca el archivo en public/import/empresas_publicas_catastro.json o en public/empresas_publicas_catastro.json`);
      const text = await res.text();
      const data = parseJsonImport(text);
      processParsedJson(data);
    } catch (e) {
      setError(e.message || 'Error cargando JSON');
      setStep('idle');
    }
  }, [processParsedJson]);

  const loadJsonFromFile = useCallback(
    (file) => {
      if (!file) return;
      setStep('loading');
      setError(null);
      setRawData(null);
      setEmpresasToInsert([]);
      setDuplicates([]);
      setImportSourceLabel(`Archivo: ${file.name}`);
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = reader.result;
          const data = parseJsonImport(typeof text === 'string' ? text : '');
          processParsedJson(data);
        } catch (e) {
          setError(e?.message || 'El archivo no es un JSON válido.');
          setStep('idle');
        }
      };
      reader.onerror = () => {
        setError('No se pudo leer el archivo.');
        setStep('idle');
      };
      reader.readAsText(file, 'UTF-8');
    },
    [processParsedJson]
  );

  const onFileInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) loadJsonFromFile(file);
    },
    [loadJsonFromFile]
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const runImport = useCallback(async () => {
    if (!empresasToInsert.length) return;
    setStep('importing');
    setError(null);
    let inserted = 0;
    let skipped = 0;
    let failed = 0;
    const batches = [];
    for (let i = 0; i < empresasToInsert.length; i += BATCH_SIZE) {
      batches.push(empresasToInsert.slice(i, i + BATCH_SIZE));
    }
    setProgress({ batch: 0, totalBatches: batches.length, inserted: 0, skipped: 0, failed: 0 });

    for (let b = 0; b < batches.length; b++) {
      const chunk = batches[b];
      const batch = writeBatch(db);
      const col = collection(db, 'empresas');
      for (const row of chunk) {
        try {
          const docRef = doc(col);
          const docData = toFirestoreDoc(row);
          batch.set(docRef, docData);
          inserted++;
        } catch (err) {
          failed++;
          console.error('Error preparando documento:', row?.nombre, err);
        }
      }
      try {
        await batch.commit();
      } catch (err) {
        failed += chunk.length;
        inserted -= chunk.length;
        console.error('Error en batch:', err);
      }
      setProgress({
        batch: b + 1,
        totalBatches: batches.length,
        inserted,
        skipped: duplicates.length,
        failed,
      });
    }

    setSummary({
      inserted,
      skipped: duplicates.length,
      failed,
      total: empresasToInsert.length + duplicates.length,
    });
    setStep('done');
    onSuccess?.();
  }, [empresasToInsert, duplicates.length, onSuccess]);

  const totalEmpresas = (rawData?.empresas?.length ?? 0) || empresasToInsert.length + duplicates.length;
  const previewList = empresasToInsert.slice(0, PREVIEW_ROWS);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Importar Empresas (JSON)</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-lg font-bold"
          >
            ×
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json,text/json"
            className="hidden"
            onChange={onFileInputChange}
            aria-label="Seleccionar archivo JSON de empresas"
          />

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          {step === 'idle' && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Elige un archivo JSON en tu equipo o carga el que está publicado en el sitio (
                <code className="bg-gray-100 px-1 rounded">public/import/empresas_publicas_catastro.json</code>
                {' '}o{' '}
                <code className="bg-gray-100 px-1 rounded">public/empresas_publicas_catastro.json</code>
                ). En producción, el JSON del hosting requiere <strong>build + deploy</strong>; con archivo local no hace falta.
              </p>
              <p className="text-sm text-gray-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <span className="font-semibold text-slate-800">Campos por fila (objeto):</span>{' '}
                <code className="text-xs break-all">
                  nombre, direccion, Region, Comuna, telefono, sitio_web, horario_atencion, descripcion, email, rubro, lat, lng
                </code>
                . Si <code className="bg-white px-1 rounded">Region</code> o <code className="bg-white px-1 rounded">Comuna</code> vienen vacíos o faltan, se guardan como cadena vacía. El array puede ir en la raíz o bajo <code className="bg-white px-1 rounded">empresas</code> / <code className="bg-white px-1 rounded">data</code> / <code className="bg-white px-1 rounded">listado</code>.
                {' '}Si el archivo trae <code className="bg-white px-1 rounded">NaN</code> (p. ej. en <code className="bg-white px-1 rounded">horario_atencion</code>), no es JSON estándar: el importador lo reemplaza por <code className="bg-white px-1 rounded">null</code> e importa igual.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2"
                >
                  📁 Buscar archivo JSON…
                </button>
                <button
                  type="button"
                  onClick={loadJson}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Cargar desde el sitio (hosting)
                </button>
              </div>
            </div>
          )}

          {step === 'loading' && (
            <div className="text-center py-8 text-gray-500">
              Cargando JSON…
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              {importSourceLabel && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-gray-800">Origen:</span> {importSourceLabel}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-600">Total en JSON</span>
                  <div className="text-xl font-bold text-gray-900">{totalEmpresas}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <span className="text-amber-700">Duplicados (omitidos)</span>
                  <div className="text-xl font-bold text-amber-800">{duplicates.length}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 col-span-2">
                  <span className="text-green-700">A insertar en Firestore</span>
                  <div className="text-xl font-bold text-green-800">{empresasToInsert.length}</div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Primeras {PREVIEW_ROWS} filas</h3>
                <div className="border rounded-lg overflow-x-auto overflow-y-auto max-h-[320px]">
                  <table className="w-full text-sm min-w-[1100px]">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2 whitespace-nowrap">nombre</th>
                        <th className="text-left p-2 whitespace-nowrap">direccion</th>
                        <th className="text-left p-2 whitespace-nowrap">Region</th>
                        <th className="text-left p-2 whitespace-nowrap">Comuna</th>
                        <th className="text-left p-2 whitespace-nowrap">telefono</th>
                        <th className="text-left p-2 whitespace-nowrap">sitio_web</th>
                        <th className="text-left p-2 whitespace-nowrap">horario_atencion</th>
                        <th className="text-left p-2 whitespace-nowrap">descripcion</th>
                        <th className="text-left p-2 whitespace-nowrap">email</th>
                        <th className="text-left p-2 whitespace-nowrap">rubro</th>
                        <th className="text-left p-2 whitespace-nowrap">lat</th>
                        <th className="text-left p-2 whitespace-nowrap">lng</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewList.map((e, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="p-2 align-top max-w-[180px]">{e.nombre || '—'}</td>
                          <td className="p-2 align-top max-w-[200px]">{e.direccion || '—'}</td>
                          <td className="p-2 align-top max-w-[120px]">{e.region ?? '—'}</td>
                          <td className="p-2 align-top max-w-[120px]">{e.comuna ?? '—'}</td>
                          <td className="p-2 align-top whitespace-nowrap">{e.telefono || '—'}</td>
                          <td className="p-2 align-top max-w-[140px] truncate" title={e.sitio_web}>{e.sitio_web || '—'}</td>
                          <td className="p-2 align-top max-w-[160px]">{e.horario_atencion || '—'}</td>
                          <td className="p-2 align-top max-w-[180px]">{e.descripcion || '—'}</td>
                          <td className="p-2 align-top max-w-[140px] truncate" title={e.email}>{e.email || '—'}</td>
                          <td className="p-2 align-top max-w-[100px]">{e.rubro || '—'}</td>
                          <td className="p-2 align-top whitespace-nowrap">{e.lat === '' || e.lat == null ? '—' : String(e.lat)}</td>
                          <td className="p-2 align-top whitespace-nowrap">{e.lng === '' || e.lng == null ? '—' : String(e.lng)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={runImport}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Importar a Firestore
                </button>
                <button
                  type="button"
                  onClick={openFilePicker}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-800 px-4 py-2 rounded-lg font-medium"
                  title="Elegir otro archivo JSON en tu equipo"
                >
                  📁 Otro archivo…
                </button>
                <button
                  type="button"
                  onClick={loadJson}
                  className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-4 py-2 rounded-lg font-medium"
                  title="Volver a leer el archivo desde la ruta del sitio (por si lo reemplazaste en hosting)"
                >
                  🔄 Recargar JSON (hosting)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('idle');
                    setError(null);
                    setImportSourceLabel('');
                  }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
                >
                  Volver
                </button>
              </div>
            </div>
          )}

          {step === 'importing' && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Batch {progress.batch} / {progress.totalBatches}
              </p>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 transition-all duration-300"
                  style={{ width: `${progress.totalBatches ? (100 * progress.batch / progress.totalBatches) : 0}%` }}
                />
              </div>
              <p className="text-sm text-gray-500">
                Insertadas: {progress.inserted} · Omitidas (duplicados): {progress.skipped} · Fallidas: {progress.failed}
              </p>
            </div>
          )}

          {step === 'done' && summary && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Resumen</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-green-50 rounded-lg p-3">
                  <span className="text-green-700">Insertadas</span>
                  <div className="text-xl font-bold text-green-800">{summary.inserted}</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <span className="text-amber-700">Omitidas (duplicados)</span>
                  <div className="text-xl font-bold text-amber-800">{summary.skipped}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <span className="text-red-700">Fallidas</span>
                  <div className="text-xl font-bold text-red-800">{summary.failed}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-700">Total en JSON</span>
                  <div className="text-xl font-bold text-gray-800">{summary.total}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
